import { Download, SlidersHorizontal } from "lucide-react";
import {
  StormImpactModelSelectionPill,
  WeatherModelSelectionPill,
  GeographicSelector,
  ModelRunSelector,
} from "@/components/shared/nav/selectors";
import { DateSelector } from "@/components/shared/nav/datepickers/DateSelector";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { parseISO, isValid } from "date-fns";
import {
  StormImpactModel,
  WeatherModel,
  GeographicNode,
  GeographicHierarchy,
} from "@/types";

interface DashboardNavHeaderProps {
  title: string;
  // Storm Impact Models
  stormImpactModels?: StormImpactModel[];
  selectedStormImpactModel?: StormImpactModel;
  onModelSelect?: (model: StormImpactModel) => void;
  stormModelsLoading?: boolean;
  // Weather Models (derived from selected storm model)
  weatherModels?: WeatherModel[];
  selectedWeatherModel?: WeatherModel;
  onWeatherModelSelect?: (model: WeatherModel) => void;
  weatherModelsLoading?: boolean;
  // Geographic Hierarchy
  geographicHierarchy?: GeographicHierarchy | null;
  geographicHierarchyLoading?: boolean;
  geographicHierarchyError?: string | null;
  onGeoUnitChange?: (geoUnit: GeographicNode) => void;
  selectedGeoNode?: GeographicNode | null;
  // Date
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  dateLoading?: boolean;
  // Model Runs
  selectedRun?: Date | null;
  availableRuns?: string[];
  runHourMap?: Record<string, string>;
  onRunSelect?: (run: Date) => void;
  runsLoading?: boolean;
  // Export
  onExportClick?: () => void;
}

export function DashboardNavHeader({
  title,
  stormImpactModels = [],
  selectedStormImpactModel,
  onModelSelect,
  stormModelsLoading = false,
  weatherModels = [],
  selectedWeatherModel,
  onWeatherModelSelect,
  weatherModelsLoading = false,
  geographicHierarchy,
  geographicHierarchyLoading = false,
  geographicHierarchyError,
  onGeoUnitChange,
  selectedGeoNode,
  selectedDate,
  onDateChange,
  dateLoading = false,
  selectedRun,
  availableRuns = [],
  runHourMap,
  onRunSelect,
  runsLoading = false,
  onExportClick,
}: DashboardNavHeaderProps) {
  const maxForecastDate = selectedWeatherModel?.max_forecast_date
    ? parseISO(selectedWeatherModel.max_forecast_date)
    : undefined;
  const validMaxForecastDate =
    maxForecastDate && isValid(maxForecastDate)
      ? maxForecastDate
      : undefined;

  const weatherModelName =
    selectedWeatherModel && "weather_model_name" in selectedWeatherModel
      ? ((selectedWeatherModel as { weather_model_name?: string | null }).weather_model_name ?? undefined)
      : undefined;

  const renderControlsContent = ({
    renderGeographyInlineTree = false,
  }: {
    renderGeographyInlineTree?: boolean;
  } = {}) => (
    <>
      {onGeoUnitChange && (
        <GeographicSelector
          geographicHierarchy={geographicHierarchy ?? null}
          onChange={onGeoUnitChange}
          pillStyle="default"
          isLoading={geographicHierarchyLoading}
          loadingError={geographicHierarchyError}
          value={selectedGeoNode}
          renderInlineTree={renderGeographyInlineTree}
        />
      )}
      {onModelSelect && (
        <StormImpactModelSelectionPill
          models={stormImpactModels}
          selectedModel={selectedStormImpactModel}
          onModelSelect={onModelSelect}
          isLoading={stormModelsLoading}
        />
      )}
      {onWeatherModelSelect && (
        <WeatherModelSelectionPill
          models={weatherModels}
          selectedModel={selectedWeatherModel}
          onModelSelect={onWeatherModelSelect}
          isLoading={weatherModelsLoading}
        />
      )}
      {onRunSelect && (
        <ModelRunSelector
          selectedRunTime={selectedRun ? selectedRun.toISOString() : null}
          onRunTimeSelect={(runTime) => {
            onRunSelect(new Date(runTime));
          }}
          availableModelRuns={availableRuns}
          runHourMap={runHourMap}
          weatherModelName={
            selectedWeatherModel?.name ||
            weatherModelName ||
            "weather model"
          }
          isLoading={runsLoading}
        />
      )}
      {onDateChange && (
        <DateSelector
          initialDate={selectedDate}
          maxDate={validMaxForecastDate}
          onDateChange={onDateChange}
          isLoading={dateLoading}
        />
      )}
    </>
  );

  return (
    <div className="flex min-w-0 items-center">
      <div className="mb-0 flex w-full min-w-0 flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="min-w-0 truncate text-lg font-bold text-foreground sm:text-xl">
          {title}
        </h1>

        <div className="hidden min-w-0 flex-wrap items-center gap-2 lg:flex">
          {renderControlsContent()}
          {onExportClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExportClick}
              className="w-full sm:w-auto"
              title="Export forecast data"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center gap-2 lg:hidden"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[min(22rem,calc(100vw-2rem))] gap-0 p-0"
          >
            <SheetHeader className="border-b px-4 py-3">
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription className="sr-only">
                Forecast filters for geography, models, run, and date.
              </SheetDescription>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:max-w-full">
              {renderControlsContent({ renderGeographyInlineTree: true })}
              {onExportClick && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExportClick}
                  className="w-full justify-center"
                  title="Export forecast data"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
